import {
  COACH_ROUTE_ACCOUNT,
  COACH_ROUTE_EXPLORE,
  COACH_ROUTE_INDEX,
  COACH_ROUTE_LEARN,
  COACH_ROUTE_PLAN,
} from '~/constants/routes/coach';

/**
 * Whether a path the browser recorded in its history is one of the shell's own.
 *
 * History entries are bare path strings — `/coach/jobs/nurse?origin=chat` — so only the pathname
 * is read: the query says which visit an entry was, never which page it was.
 */
export const isCoachShellEntry = (entry: unknown): boolean => {
  if (typeof entry !== 'string') {
    return false;
  }

  const pathname = entry.split(/[?#]/)[0] ?? '';

  return pathname === COACH_ROUTE_INDEX || pathname.startsWith(`${COACH_ROUTE_INDEX}/`);
};

/**
 * Where closing an activity takes the student: back to what they opened it from, or to the bare
 * shell when what they opened it from was not the shell at all.
 *
 * Closing is going back, because opening was arriving — the student lands on the list they picked
 * the thing off, with its search and its scroll where they left them. But the entry behind an
 * activity somebody linked them straight into belongs to whatever they were doing before this app,
 * and stepping onto it takes the coach down with it: the shell unmounts mid-close, the credenza it
 * was animating goes with it, and the visit the student just ended is never reported closed
 * because the watcher that reports it left too. A close with nowhere of ours to go back to
 * therefore goes to the bare shell, which is where going back was taking them anyway.
 */
export const coachCloseTarget = (previousEntry: unknown): 'back' | 'shell' =>
  (isCoachShellEntry(previousEntry) ? 'back' : 'shell');

export interface CoachActivityLeaveDeps {
  /** Tells the outlet the next move is a close, so the surface being left gets its animation. */
  markClosing: () => void;
  /** What the browser recorded behind the current entry, read at the moment of leaving. */
  previousEntry: () => unknown;
  /** Steps back onto the page the activity was opened from. Settles when the router has landed. */
  back: () => Promise<unknown>;
  /**
   * Goes to the bare shell instead, for a close with nowhere of ours to go back to, or to the
   * `fallback` page the close names when it has one.
   */
  toShell: (fallback?: string) => Promise<unknown>;
}

/**
 * Leaving the activity, once per leaving.
 *
 * A close arrives from more places than the chrome's own button: an activity that finishes calls
 * `completeFlow`, one that is backed out of calls `cancelFlow`, and a 404 inside a credenza offers
 * its own way out. They are all this, and none of them can see the others. Meanwhile the thing that
 * ends a close is a navigation, which takes a frame or two to happen — so between the press and the
 * landing the URL still names the activity and every guard downstream still reads it as open. A
 * second press in that window is not a second close; it is the same one, asked for twice, and
 * honoured twice it pops two history entries and drops the student a page further back than they
 * ever asked to go.
 *
 * So the leaving itself is what is held, from the moment it is asked for until the router has
 * landed — however it lands, since a navigation a guard turned away is still a leaving that is over
 * and the way out must not be dead afterwards.
 */
export const createCoachActivityLeave = ({
  markClosing,
  previousEntry,
  back,
  toShell,
}: CoachActivityLeaveDeps) => {
  let leaving = false;

  return (fallback?: string) => {
    if (leaving) {
      return;
    }
    leaving = true;
    markClosing();

    const landed = coachCloseTarget(previousEntry()) === 'back' ? back() : toShell(fallback);

    void landed.catch(() => {}).finally(() => {
      leaving = false;
    });
  };
};

/** A place in the IA's bar that the back button can name. */
export type CoachBackPlace = 'chat' | 'learn' | 'explore' | 'plan' | 'profile';

/** Where each of the bar's places lives. */
export const COACH_BACK_PLACE_ROUTES: Record<CoachBackPlace, string> = {
  chat: COACH_ROUTE_INDEX,
  learn: COACH_ROUTE_LEARN,
  explore: COACH_ROUTE_EXPLORE,
  plan: COACH_ROUTE_PLAN,
  profile: COACH_ROUTE_ACCOUNT,
};

export const pathnameOf = (entry: string) => entry.split(/[?#]/)[0] ?? '';

/** The place whose segment, under `root`, the page sits at or below. */
export const placeUnder = <P extends string>({ pathname, root, pages }: { pathname: string; root: string; pages: [string, P][] }): P | null =>
  pages.find(([segment]) => {
    const prefix = `${root}/${segment}`;

    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  })?.[1] ?? null;

/** The places' own pages, and the pages whose list a place is. */
const PLACE_PAGES: [string, CoachBackPlace][] = [
  ['learn', 'learn'],
  ['badges', 'learn'],
  ['explore', 'explore'],
  ['plan', 'plan'],
  ['portfolio', 'plan'],
  ['saved', 'plan'],
  ['saved-pathways', 'plan'],
  ['account', 'profile'],
  ['profile', 'profile'],
];

/** The pages a place opens, which are its own however the student arrived on them. */
export const OPENED_PAGES: [string, CoachBackPlace][] = [
  ['jobs', 'explore'],
  ['institutions', 'explore'],
  ['courses', 'explore'],
  ['cdp', 'plan'],
  ['career-advice', 'explore'],
  ['articles', 'learn'],
  ['videos', 'learn'],
];

const placeOf = (pathname: string, pages: [string, CoachBackPlace][]) => placeUnder({ pathname, root: COACH_ROUTE_INDEX, pages });

/**
 * The place in the bar a page belongs to: the one the bar marks current on it. A job, an
 * institution, a course or an industry is Explore's; an article, a video, a collection or a badge is
 * Learn's; anything else of the shell's is the chat's.
 */
export const coachHomePlace = (path: string): CoachBackPlace => {
  const pathname = pathnameOf(path);

  return placeOf(pathname, PLACE_PAGES) ?? placeOf(pathname, OPENED_PAGES) ?? 'chat';
};

/**
 * Which of the bar's places going back lands on, so the back button can say so: the place whose
 * page is behind this one, the chat for the bare shell, and none for a page that is no place of the
 * bar's (another job, say), which the button names as plain back. Undefined when there is nothing
 * of ours behind this page to go back to.
 */
export const coachBackPlace = (previousEntry: unknown): CoachBackPlace | null | undefined => {
  if (coachCloseTarget(previousEntry) === 'shell') {
    return undefined;
  }

  const pathname = pathnameOf(previousEntry as string);
  if (pathname === COACH_ROUTE_INDEX) {
    return 'chat';
  }

  return placeOf(pathname, PLACE_PAGES);
};
